import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { ServiceCard, Loader, PageHero } from '../components/Shared';
import ConstructionCanvas from '../components/ConstructionCanvas';

const CRANE = '#d39816';
const CW    = { position: 'absolute', inset: 0, zIndex: 0 };
const CZ    = { position: 'relative', zIndex: 2 };

// ════════════════════════════════════════════════════════
// SERVICES PAGE
// ════════════════════════════════════════════════════════
export function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  useEffect(() => { api.get('/services').then(r => setServices(r.data.data || [])).finally(() => setLoading(false)); }, []);

  const process = [
    { step: '01', t: 'Consultation',  d: 'In-depth discussion to understand your vision, scope, and budget.' },
    { step: '02', t: 'Design & Plan', d: 'Our architects create detailed plans ensuring every detail is considered.' },
    { step: '03', t: 'Permits',       d: 'We handle all regulatory approvals and ensure full code compliance.' },
    { step: '04', t: 'Construction',  d: 'Expert teams execute with precision, maintaining quality at every stage.' },
    { step: '05', t: 'QA / Control',  d: 'Rigorous checks throughout — nothing is left to chance.' },
    { step: '06', t: 'Handover',      d: 'Full documentation and post-completion support included.' },
  ];

  return (
    <>
      <PageHero label="What We Offer" title="Our" accent="Services" subtitle="Comprehensive construction solutions tailored to your vision." image="/hero-services.jpg" pageClass="services" />

      {/* Services Introduction */}
      <section style={{ position: 'relative', background: 'var(--site-bg-3)', padding: '56px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="light" color={CRANE} opacity={0.3} /></div>
        <div className="container" style={CZ}>
          <div className="text-center mb-3">
            <span className="section-label">Comprehensive Solutions</span>
            <h2 className="section-title dark-text">Why Our <span>Services</span></h2>
            <span className="divider-accent center" />
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <p style={{ color: 'var(--text-2)', fontSize: '1.05rem', lineHeight: 1.9, marginBottom: 16 }}>
                At Bacha & Son, we don't just deliver projects — we craft legacies. Our comprehensive range of services combines innovative design, rigorous engineering, and meticulous execution to transform your vision into reality.
              </p>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.85, marginBottom: 0 }}>
                With over two decades of excellence, our expert teams bring unparalleled quality and craftsmanship to every residential, commercial, and industrial project. From conception to completion, we ensure every detail reflects our commitment to building exceptional structures that stand the test of time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '56px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="structure" color={CRANE} opacity={0.4} /></div>
        <div className="container" style={CZ}>
          {loading ? <div className="text-center py-5"><div className="spinner dark mx-auto" /></div> : (
            <div className="row g-4">
              {services.map((s, i) => (
                <div key={s.id} className="col-md-6 col-lg-4" style={{ animation: `fadeInUp 0.55s ${0.07 * i}s ease both` }}>
                  <ServiceCard service={s} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Process */}
      <section style={{ position: 'relative', background: 'var(--site-bg-3)', padding: '26px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="blueprint" color={CRANE} opacity={0.35} /></div>
        <div className="container" style={CZ}>
          <div className="text-center mb-5">
            <span className="section-label">How We Work</span>
            <h2 className="section-title dark-text">Our <span>Process</span></h2>
            <span className="divider-accent center" />
          </div>
          <div className="row g-4">
            {process.map((p, i) => (
              <div key={i} className="col-md-6 col-lg-4" style={{ animation: `fadeInUp 0.55s ${0.08 * i}s ease both` }}>
                <div className="process-card" style={{ padding: '32px 24px', background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid rgba(211,152,22,0.12)', borderRadius: 'var(--radius-lg)', height: '100%', position: 'relative', overflow: 'hidden', transition: 'var(--transition)' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.12)'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 36, height: 36, background: CRANE, color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.75rem', flexShrink: 0 }}>{p.step}</div>
                    <h5 style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', margin: 0 }}>{p.t}</h5>
                  </div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.8, margin: 0 }}>{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ════════════════════════════════════════════════════════
// ABOUT PAGE
// ════════════════════════════════════════════════════════
export function AboutPage() {
  const [settings, setSettings] = useState({});
  const [team, setTeam]         = useState([]);
  useEffect(() => {
    api.get('/settings').then(r => setSettings(r.data.data || {})).catch(() => {});
    api.get('/team').then(r => setTeam(r.data.data || [])).catch(() => {});
  }, []);
  const s = settings;
  const stats = [
    { v: s.site_years_experience    || '25',  l: 'Years Experience',   suf: '+' },
    { v: s.site_projects_completed  || '500', l: 'Projects Completed', suf: '+' },
    { v: s.site_happy_clients       || '350', l: 'Happy Clients',      suf: '+' },
    { v: s.site_team_members        || '120', l: 'Team Members',       suf: '+' },
  ];

  return (
    <>
      <PageHero label="Our Story" title="About" accent="Bacha & Son" subtitle="Two decades of building excellence, one landmark project at a time." image="/hero-about.jpg" pageClass="about" />

      {/* Story + Stats */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '56px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="blueprint" color={CRANE} opacity={0.38} /></div>
        <div className="container" style={CZ}>
          <div className="row g-5 align-items-center">
            <div className="col-lg-6" style={{ animation: 'slideInL 0.6s ease both' }}>
              <span className="section-label">Who We Are</span>
              <h2 className="section-title dark-text">A Legacy of <span>Excellence</span></h2>
              <span className="divider-accent" />
              <p style={{ color: 'var(--text-2)', lineHeight: 2, marginBottom: 16 }}>
                {s.site_about || 'Bacha & Son Construction is a premier construction company with over 25 years of experience delivering exceptional residential, commercial, and industrial projects.'}
              </p>
              <p style={{ color: 'var(--text-muted)', lineHeight: 2 }}>Our 120+ professionals bring unparalleled expertise across architectural design, structural engineering, interior finishing, and landscape design.</p>
            </div>
            <div className="col-lg-6">
              <div className="row g-3">
                {stats.map((st, i) => (
                  <div key={i} className="col-6" style={{ animation: `fadeInUp 0.55s ${0.1 * i}s ease both` }}>
                    <div style={{ background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)', border: '1px solid rgba(211,152,22,0.15)', borderRadius: 'var(--radius-lg)', padding: '28px 20px', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
                      <div className="stat-number-lg" style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', fontWeight: 700, color: CRANE, lineHeight: 1 }}>{st.v}{st.suf}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 8 }}>{st.l}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      {team.length > 0 && (
        <section style={{ position: 'relative', background: 'var(--site-bg-3)', padding: '26px 0', overflow: 'hidden' }}>
          <div style={CW}><ConstructionCanvas variant="crane" color={CRANE} opacity={0.42} /></div>
          <div className="container" style={CZ}>
            <div className="text-center mb-5">
              <span className="section-label">The People</span>
              <h2 className="section-title dark-text">Meet Our <span>Leadership</span></h2>
              <span className="divider-accent center" />
            </div>
            <div className="row g-4 justify-content-center">
              {team.map((m, i) => (
                <div key={m.id} className="col-md-6 col-lg-3" style={{ animation: `fadeInUp 0.55s ${0.1 * i}s ease both` }}>
                  <div style={{ background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)', border: '1px solid rgba(211,152,22,0.12)', borderRadius: 'var(--radius-lg)', padding: 28, textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ width: 72, height: 72, borderRadius: '50%', background: `linear-gradient(135deg,${CRANE},#8B6508)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: '#fff', fontFamily: 'var(--font-display)', margin: '0 auto 16px' }}>
                      {m.name.charAt(0)}
                    </div>
                    <h6 style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', marginBottom: 4 }}>{m.name}</h6>
                    <div style={{ color: CRANE, fontSize: '0.8rem', fontWeight: 600, letterSpacing: 1, marginBottom: 10 }}>{m.position}</div>
                    {m.bio && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.7 }}>{m.bio}</p>}
                    {m.email && <a href={`mailto:${m.email}`} style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}><i className="bi bi-envelope me-1" />{m.email}</a>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Values */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '26px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="full" color={CRANE} opacity={0.3} /></div>
        <div className="container" style={CZ}>
          <div className="text-center mb-5">
            <span className="section-label">Our Foundation</span>
            <h2 className="section-title dark-text">Core <span>Values</span></h2>
            <span className="divider-accent center" />
          </div>
          <div className="row g-4">
            {[
              { icon: 'bi-shield-fill-check', t: 'Integrity',   d: 'Absolute transparency, honesty, and ethical standards in every relationship.' },
              { icon: 'bi-star-fill',         t: 'Excellence',  d: 'Every detail executed to the highest standard — no compromise.' },
              { icon: 'bi-lightbulb-fill',    t: 'Innovation',  d: 'Continuously adopting new technologies and methods for smarter solutions.' },
              { icon: 'bi-people-fill',       t: 'Partnership', d: 'Long-term partnerships that extend far beyond project completion.' },
            ].map((v, i) => (
              <div key={i} className="col-md-6 col-lg-3" style={{ animation: `fadeInUp 0.55s ${0.1 * i}s ease both` }}>
                <div style={{ padding: '36px 24px', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)', border: '1px solid rgba(211,152,22,0.12)', borderRadius: 'var(--radius-lg)', textAlign: 'center', height: '100%', transition: 'var(--transition)', boxShadow: 'var(--shadow-sm)' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; e.currentTarget.style.borderColor = `rgba(211,152,22,0.3)`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.12)'; }}>
                  <i className={`bi ${v.icon}`} style={{ fontSize: 34, color: CRANE, marginBottom: 18, display: 'block' }} />
                  <h5 style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', marginBottom: 10 }}>{v.t}</h5>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.8, margin: 0 }}>{v.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ════════════════════════════════════════════════════════
// CONTACT PAGE
// ════════════════════════════════════════════════════════
export function ContactPage() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');
  useEffect(() => { api.get('/settings').then(r => setSettings(r.data.data || {})).catch(() => {}); }, []);

  const handleSubmit = async e => {
    e.preventDefault(); setSending(true); setError('');
    try { await api.post('/contact', form); setSent(true); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }
    catch (err) { setError(err.response?.data?.message || 'Failed to send. Please try again.'); }
    finally { setSending(false); }
  };

  const officeAddress = settings.site_address || '123 Construction Boulevard, Suite 500, Metro City, MC 10001';

  return (
    <>
      <PageHero label="Get in Touch" title="Contact" accent="Us" subtitle="Have a project in mind? We'd love to bring your vision to life." image="/hero-contact.jpg" pageClass="contact" />

      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '56px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="blueprint" color={CRANE} opacity={0.35} /></div>
        <div className="container" style={CZ}>
          <div className="row g-5">
            {/* Info */}
            <div className="col-lg-5" style={{ animation: 'slideInL 0.6s ease both' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 16 }}>Let's Talk About Your Project</h3>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.9, marginBottom: 32 }}>Our team is ready to discuss your needs. Fill in the form and we'll respond within one business day.</p>
              {[
                { icon: 'bi-geo-alt-fill',   l: 'Address', v: settings.site_address },
                { icon: 'bi-telephone-fill', l: 'Phone',   v: settings.site_phone },
                { icon: 'bi-envelope-fill',  l: 'Email',   v: settings.site_email },
              ].filter(c => c.v).map(c => (
                <div key={c.l} style={{ display: 'flex', gap: 14, marginBottom: 22, alignItems: 'flex-start' }}>
                  <div style={{ width: 44, height: 44, background: `rgba(211,152,22,0.1)`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`bi ${c.icon}`} style={{ color: CRANE, fontSize: 18 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 3 }}>{c.l}</div>
                    <div style={{ color: 'var(--text)', fontSize: '0.95rem' }}>{c.v}</div>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 28, padding: '18px 22px', background: `rgba(211,152,22,0.06)`, border: `1px solid rgba(211,152,22,0.15)`, borderRadius: 'var(--radius-lg)' }}>
                <div style={{ fontWeight: 600, color: CRANE, marginBottom: 6, fontSize: '0.875rem' }}>Business Hours</div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0, lineHeight: 1.8 }}>
                  Mon–Fri: 8:00 AM – 6:00 PM<br />Saturday: 9:00 AM – 1:00 PM<br />Sunday: Closed
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="col-lg-7" style={{ animation: 'fadeInUp 0.6s 0.1s ease both' }}>
              <div style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', border: `1px solid rgba(211,152,22,0.15)`, borderRadius: 'var(--radius-xl)', padding: 40, boxShadow: 'var(--shadow)' }}>
                {sent ? (
                  <div className="text-center py-4">
                    <div style={{ width: 68, height: 68, background: 'rgba(45,155,111,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
                      <i className="bi bi-check-lg" style={{ fontSize: 32, color: 'var(--success)' }} />
                    </div>
                    <h4 style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', marginBottom: 10 }}>Message Sent!</h4>
                    <p style={{ color: 'var(--text-muted)' }}>We'll get back to you within one business day.</p>
                    <button onClick={() => setSent(false)} className="btn-primary-pub mt-3">Send Another</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      <div className="col-sm-6"><label className="form-label-styled">Full Name *</label><input type="text" className="form-control-styled" required placeholder="John Smith" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
                      <div className="col-sm-6"><label className="form-label-styled">Email *</label><input type="email" className="form-control-styled" required placeholder="john@company.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
                      <div className="col-sm-6"><label className="form-label-styled">Phone</label><input type="tel" className="form-control-styled" placeholder="+1 (555) 000-0000" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
                      <div className="col-sm-6"><label className="form-label-styled">Subject</label><input type="text" className="form-control-styled" placeholder="Project Inquiry" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} /></div>
                      <div className="col-12"><label className="form-label-styled">Message *</label><textarea className="form-control-styled" rows={6} required placeholder="Tell us about your project..." value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} /></div>
                    </div>
                    {error && <div style={{ marginTop: 14, padding: '12px 16px', background: 'rgba(201,64,64,0.06)', border: '1px solid rgba(201,64,64,0.2)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '0.875rem' }}>{error}</div>}
                    <button type="submit" className="btn-primary-pub w-100 mt-4" disabled={sending} style={{ justifyContent: 'center' }}>
                      {sending ? <><div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', marginRight: 8, display: 'inline-block' }} />Sending...</> : <><i className="bi bi-send" /> Send Message</>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LOCATION MAP — office location ── */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '20px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="blueprint" color={CRANE} opacity={0.45} /></div>
        <div className="container" style={CZ}>
          <div className="text-center mb-4">
            <span className="section-label">Find Us</span>
            <h2 className="section-title dark-text">Visit Our <span>Office</span></h2>
            <span className="divider-accent center" />
          </div>
          <div className="home-map-frame" style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow)', border: '1px solid rgba(211,152,22,0.15)', height: 380 }}>
            <iframe
              title="Office Location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(officeAddress)}&output=embed`}
              width="100%" height="100%" style={{ border: 0, display: 'block' }} loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="text-center mt-4">
            <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', margin: 0 }}>
              <i className="bi bi-geo-alt-fill me-2" style={{ color: CRANE }} />{officeAddress}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
